// A specialist's tracked dates as a table in the Next actions table's look: the dates no
// open action is working towards (expiries, build-by dates before work starts, their
// deadlines, missed dates nobody picked up), closest first. A date an open action serves
// shows on that action's row instead, so here it waits behind a count, which a link to
// it opens. See docs/Y2026-Q4/design-structured-next-actions-and-dates.md 7.2.
import { useMemo } from "react";
import { Link } from "react-router-dom";

import { areaLabel } from "../api/hooks/buildingReadiness";
import type {
  NextAction,
  ProjectAgentStatusList,
  TrackedDate,
} from "../api/hooks/useProjectAgentStatus";
import { projectSpecialistPath } from "../routes/learningAgentPaths";
import { formatDay } from "../utils/attributeFormat";
import {
  type DateTone,
  NEAR_EXTERNAL_DAYS,
  NEAR_INTERNAL_DAYS,
  daysUntil,
  hardnessLabel,
  projectItems,
  servingActions,
  toneFor,
  typeLine,
} from "../utils/nextActions";
import { useHashOpen } from "../utils/useScrollToHash";
import { Cited } from "./Cited";
import { CoverChip } from "./NextActionTable";
import { isTextSelectionClick } from "./selectableButton";
import styles from "./NextActionTable.module.css";
import { Chevron } from "./Chevron";

// Must agree with DATE_KINDS in inrange_agents/next_actions.py.
const KIND_LABEL: Record<string, string> = {
  expiry: "Expiry",
  accept_by: "Accept by",
  build_by: "Build by",
  test_by: "Test by",
  payment_due: "Payment due",
  deliver_by: "Deliver by",
  promised_response: "Promised response",
  milestone: "Milestone",
};

const TONE_CLASS: Record<DateTone, string> = {
  "": "",
  near: styles.near,
  missed: styles.missed,
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

interface DateRow {
  date: TrackedDate;
  serving: { agent: string; action: NextAction }[];
  /** Days until the date; negative once passed; null when it has none yet. */
  days: number | null;
  /** Nothing works towards it, though it is missed or its work should have started. */
  nobody: boolean;
  /** Days until work towards it starts, while that is still ahead and nothing serves it. */
  startsIn: number | null;
  /** The date's own colour: how near it is, or passed. */
  dateTone: DateTone;
  /** The row's: the date's, or red when nobody is on it. */
  tone: DateTone;
}

const toRow = (
  date: TrackedDate,
  serving: DateRow["serving"],
  today: string,
): DateRow => {
  const days = date.date ? daysUntil(date.date, today) : null;
  const passed = days !== null && days < 0;
  const unserved = serving.length === 0 && !date.no_action_reason;
  const workDue =
    date.hardness === "hard" &&
    !!date.prepare_from &&
    date.prepare_from <= today;
  const nobody = unserved && (passed || workDue);
  const startsIn =
    unserved && !nobody && date.prepare_from && date.prepare_from > today
      ? daysUntil(date.prepare_from, today)
      : null;
  const dateTone: DateTone = date.date
    ? toneFor(
        date.date,
        today,
        date.hardness === "hard" ? NEAR_EXTERNAL_DAYS : NEAR_INTERNAL_DAYS,
      )
    : "";
  const tone: DateTone = nobody
    ? "missed"
    : dateTone ||
      (startsIn !== null && startsIn <= NEAR_EXTERNAL_DAYS ? "near" : "");
  return { date, serving, days, nobody, startsIn, dateTone, tone };
};

const DateStatus = ({ row }: { row: DateRow }) => {
  const { date, days, serving } = row;
  const near = row.dateTone === "near" ? styles.near : "";
  const startsNear =
    row.startsIn !== null && row.startsIn <= NEAR_EXTERNAL_DAYS
      ? styles.near
      : "";
  return (
    <span className={styles.chips}>
      {days !== null &&
        (days < 0 ? (
          <span className={`${styles.chip} ${styles.missed}`}>
            Missed {plural(-days, "day")} ago
          </span>
        ) : (
          <span className={`${styles.chip} ${near}`}>
            {days === 0 ? "Today" : `In ${plural(days, "day")}`}
          </span>
        ))}
      {serving.length > 0 && (
        <span className={styles.chip}>{plural(serving.length, "action")}</span>
      )}
      {(date.no_action_reason || row.nobody) && (
        <CoverChip reason={date.no_action_reason} />
      )}
      {row.startsIn !== null && (
        <span className={`${styles.chip} ${startsNear}`}>
          Work starts {formatDay(date.prepare_from)}
        </span>
      )}
    </span>
  );
};

const DateDetails = ({
  row,
  projectId,
}: {
  row: DateRow;
  projectId: string;
}) => {
  const { date, days, serving } = row;
  const passed = days !== null && days < 0;
  return (
    <div className={styles.sections}>
      {date.consequence && (
        <section className={styles.section}>
          <h5>
            {passed
              ? "What happens now it's missed"
              : "What happens if we miss this date"}
          </h5>
          <p>{date.consequence}</p>
        </section>
      )}
      {serving.length > 0 && (
        <section className={styles.section}>
          <h5>Working towards it</h5>
          <ul>
            {serving.map(({ agent, action }) => (
              <li key={action.action_id}>
                <Link
                  to={`${projectSpecialistPath(projectId, agent)}#${action.action_id}`}
                >
                  {action.title}
                </Link>{" "}
                <span className={styles.small}>{typeLine(action)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {row.nobody && (
        <section className={styles.section}>
          <h5>Why nobody is on it</h5>
          <p>
            No action is working towards it.
            {date.prepare_from &&
              ` Work had to start by ${formatDay(date.prepare_from)}.`}
          </p>
        </section>
      )}
      {date.no_action_reason && (
        <section className={styles.section}>
          <h5>Why no action is needed</h5>
          <p>{date.no_action_reason}</p>
        </section>
      )}
      {(date.source || date.upload_id) && (
        <section className={styles.section}>
          <h5>Why this date</h5>
          <p>
            <Cited
              text={`Set by ${date.source || `[${date.upload_id}]`}.`}
              inline
            />
          </p>
        </section>
      )}
      {row.startsIn !== null && (
        <section className={styles.section}>
          <h5>When work starts</h5>
          <p>
            {formatDay(date.prepare_from)}. From then on an action has to be
            working towards it.
          </p>
        </section>
      )}
    </div>
  );
};

const DateRows = ({
  rows,
  openIds,
  onToggle,
  projectId,
  showArea,
}: {
  rows: DateRow[];
  openIds: Set<string>;
  onToggle: (id: string) => void;
  projectId: string;
  showArea: boolean;
}) => (
  <div className={styles.scroll}>
    <table className={styles.table}>
      <colgroup>
        <col className={styles.titleCol} />
        <col className={styles.dateCol} />
        <col />
      </colgroup>
      <thead>
        <tr>
          <th className={styles.titleHead}>Deadline</th>
          <th>Date</th>
          <th>Status</th>
        </tr>
      </thead>
      {rows.map((row) => {
        const { date } = row;
        const open = openIds.has(date.date_id);
        const kind = KIND_LABEL[date.kind] ?? date.kind.replace(/_/g, " ");
        const area = showArea ? areaLabel(date.area) : "";
        return (
          <tbody
            key={date.date_id}
            id={date.date_id}
            className={`${styles.item} ${TONE_CLASS[row.tone]}`}
          >
            <tr
              className={styles.row}
              onClick={(e) => {
                if (!isTextSelectionClick(e)) onToggle(date.date_id);
              }}
            >
              <td className={styles.titleCell}>
                <button
                  type="button"
                  className={styles.toggle}
                  aria-expanded={open}
                >
                  <span className={styles.caret} aria-hidden="true">
                    <Chevron tree open={open} />
                  </span>
                  <span className={styles.titleText}>
                    <strong>{date.what}</strong>
                    <span className={styles.typeLine}>
                      {area && `${area} · `}
                      {kind} · {hardnessLabel(date.hardness)}
                    </span>
                  </span>
                </button>
              </td>
              <td>
                {date.date ? (
                  <span
                    className={`${styles.chip} ${TONE_CLASS[row.dateTone]}`}
                  >
                    {formatDay(date.date)}
                  </span>
                ) : (
                  <span className={styles.dash}>
                    {date.state === "none" ? "No date" : "Not given yet"}
                  </span>
                )}
              </td>
              <td>
                <DateStatus row={row} />
              </td>
            </tr>
            {open && (
              <tr className={styles.detailRow}>
                <td colSpan={3}>
                  <DateDetails row={row} projectId={projectId} />
                </td>
              </tr>
            )}
          </tbody>
        );
      })}
    </table>
  </div>
);

/** An agent's open tracked dates, closest first, narrowed by ``include`` (Building
 * readiness shows each area's inside the area). ``collapseCovered`` puts the dates an
 * action works towards behind a count; ``showArea`` names each date's area. */
export const DateTable = ({
  agent,
  projectId,
  data,
  include = () => true,
  empty,
  collapseCovered,
  showArea,
}: {
  agent: string;
  projectId: string;
  data: ProjectAgentStatusList;
  include?: (d: TrackedDate) => boolean;
  empty: string;
  collapseCovered: boolean;
  showArea: boolean;
}) => {
  const today = data.lists_today;
  const rows = useMemo(() => {
    const items = projectItems(data);
    return (
      data.agents.find((a) => a.agent === agent)?.lists?.tracked_dates ?? []
    )
      .filter((d) => !d.closed && include(d))
      .sort((a, b) => (a.date || "9999").localeCompare(b.date || "9999"))
      .map((d) => toRow(d, servingActions(items, d.date_id), today));
  }, [data, agent, include, today]);
  const watched = collapseCovered
    ? rows.filter((r) => r.serving.length === 0)
    : rows;
  const covered = collapseCovered
    ? rows.filter((r) => r.serving.length > 0)
    : [];

  const { landedId, openIds, toggle } = useHashOpen();

  if (rows.length === 0) return <p className={styles.muted}>{empty}</p>;
  return (
    <>
      {watched.length > 0 ? (
        <DateRows
          rows={watched}
          openIds={openIds}
          onToggle={toggle}
          projectId={projectId}
          showArea={showArea}
        />
      ) : (
        <p className={styles.muted}>
          Every tracked date has an action working towards it.
        </p>
      )}
      {covered.length > 0 && (
        <details
          className={styles.history}
          open={covered.some((r) => r.date.date_id === landedId)}
        >
          <summary>
            {plural(covered.length, "more date")} with actions working towards
            them
          </summary>
          <DateRows
            rows={covered}
            openIds={openIds}
            onToggle={toggle}
            projectId={projectId}
            showArea={showArea}
          />
        </details>
      )}
    </>
  );
};
